import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCartSimple, FileText } from "@phosphor-icons/react";
import { formatCurrency, DEFAULT_PRODUCT_IMAGE } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.4) }}
    >
      <Link
        to={`/product/${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="group flex h-full flex-col border border-cyan/15 bg-navy-surface transition-all duration-300 hover:border-cyan/40 hover:shadow-glow-sm"
      >
        <div className="relative aspect-square overflow-hidden bg-navy-deep">
          <img
            src={product.image_url || DEFAULT_PRODUCT_IMAGE}
            alt={product.name}
            className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              if (e.currentTarget.src !== DEFAULT_PRODUCT_IMAGE) e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
            }}
          />
          {product.purity && (
            <span className="absolute left-3 top-3 border border-cyan/30 bg-navy/80 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-cyan backdrop-blur">
              {product.purity}
            </span>
          )}
          {product.coa_file_path && (
            <span className="absolute right-3 top-3 flex items-center gap-1 border border-cyan/20 bg-navy/80 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-teal backdrop-blur">
              <FileText size={11} /> COA
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <span className="font-mono text-[10px] uppercase tracking-widest text-teal">
            {product.category}
          </span>
          <h3 className="mt-1 font-heading text-lg font-bold tracking-tight text-white transition-colors group-hover:text-cyan">
            {product.name}
          </h3>
          <p className="mt-1.5 line-clamp-2 flex-1 text-sm leading-relaxed text-teal-muted">
            {product.short_description}
          </p>

          <div className="mt-4 flex items-center justify-between border-t border-cyan/10 pt-4">
            <span className="font-mono text-lg font-bold text-white">
              {formatCurrency(product.price)}
            </span>
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={handleAdd}
              className="flex items-center gap-1.5 bg-cyan px-3 py-2 font-mono text-[11px] font-semibold uppercase tracking-wider text-navy-deep transition-all duration-300 hover:bg-cyan-bright hover:shadow-glow"
            >
              <ShoppingCartSimple size={15} weight="bold" /> Add
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
